import { FC, useCallback, useMemo, useRef, useState } from 'react';
import type { ProdutoCatalogo } from '@/types/dashboards';
import { alternarGrupo, nomeDoProduto, porFormato, produtosQueCasam } from '@/utils/filtroPaineis';
import { useFechaFora } from '@/hooks/useFechaFora';
import Popover from './Popover';

interface Props {
  catalogo: ProdutoCatalogo[];
  carregando: boolean;
  selecionados: string[];
  onChange: (codigos: string[]) => void;
}

const ListaProdutos: FC<{
  grupos: { formato: string; produtos: ProdutoCatalogo[] }[];
  rascunho: string[];
  onRascunho: (codigos: string[]) => void;
}> = ({ grupos, rascunho, onRascunho }) => {
  if (grupos.length === 0) {
    return <p className="px-2 py-6 text-center text-sm text-content-muted">Nenhum produto com esse nome ou código.</p>;
  }
  return (
    <div className="max-h-80 overflow-y-auto pr-1" data-testid="filtro-produtos-lista">
      {grupos.map((g) => {
        const todos = g.produtos.every((p) => rascunho.includes(p.codigo));
        return (
          <fieldset key={g.formato} className="mb-2">
            <legend className="flex w-full items-center justify-between px-1 py-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-content-muted">{g.formato}</span>
              <button
                type="button"
                onClick={() => onRascunho(alternarGrupo(rascunho, g.produtos))}
                aria-label={`${todos ? 'Desmarcar' : 'Marcar'} todos de ${g.formato}`}
                className="text-xs text-accent hover:underline"
              >
                {todos ? 'Desmarcar' : 'Marcar'} todos ({g.produtos.length})
              </button>
            </legend>
            {g.produtos.map((p) => (
              <label
                key={p.codigo}
                className="flex cursor-pointer items-start gap-2 rounded-md px-1 py-1 text-sm hover:bg-surface-hover"
              >
                <input
                  type="checkbox"
                  className="mt-0.5 accent-[var(--accent)]"
                  checked={rascunho.includes(p.codigo)}
                  onChange={(e) =>
                    onRascunho(e.target.checked ? [...rascunho, p.codigo] : rascunho.filter((c) => c !== p.codigo))
                  }
                />
                <span className="min-w-0">
                  <span className="font-mono text-xs tabular-nums text-content-secondary">{p.codigo}</span>{' '}
                  <span className="text-content-primary">{p.descricao || 'Sem descrição'}</span>
                </span>
              </label>
            ))}
          </fieldset>
        );
      })}
    </div>
  );
};

/**
 * Filtro de produto: um ou vários, buscando pelo código (com ou sem ponto) ou pelo nome,
 * agrupados por formato. As marcações valem só ao clicar "Aplicar" — cada mudança
 * remonta a aba no servidor. Os escolhidos aparecem como etiquetas, removíveis uma a uma.
 */
const SeletorProdutos: FC<Props> = ({ catalogo, carregando, selecionados, onChange }) => {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [rascunho, setRascunho] = useState<string[]>(selecionados);
  const ancora = useRef<HTMLDivElement>(null);
  const fechar = useCallback(() => setAberto(false), []);
  useFechaFora(ancora, aberto, fechar);

  const grupos = useMemo(() => porFormato(produtosQueCasam(catalogo, busca)), [catalogo, busca]);

  const abrir = () => {
    setRascunho(selecionados);
    setBusca('');
    setAberto((v) => !v);
  };

  const aplicar = () => {
    onChange(rascunho);
    setAberto(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 text-xs uppercase tracking-wide text-content-muted">Produtos</span>
      {/* Sem `relative`: a lista abre embaixo da barra de filtros, inteira na tela (ver Popover). */}
      <div ref={ancora}>
        <button
          type="button"
          onClick={abrir}
          aria-expanded={aberto}
          data-testid="filtro-produtos"
          className="inline-flex items-center gap-1 rounded-full border border-theme-border px-3 py-1 text-xs text-content-secondary hover:text-content-primary"
        >
          <span aria-hidden className="material-symbols-outlined text-[14px] leading-none">
            inventory_2
          </span>
          {selecionados.length === 0 ? 'Todos' : `${selecionados.length} escolhido${selecionados.length > 1 ? 's' : ''}`}
          <span aria-hidden className="material-symbols-outlined text-[14px] leading-none">
            expand_more
          </span>
        </button>
        {aberto && (
          <Popover rotulo="Escolher produtos" testId="filtro-produtos-painel">
            <div className="flex w-[28rem] max-w-full flex-col gap-2">
              <input
                type="search"
                autoFocus
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por nome ou código (ex.: 111.0716, fisiológico 100)"
                aria-label="Buscar produto"
                className="w-full rounded-lg border border-theme-border bg-surface-primary px-3 py-2 text-sm text-content-primary placeholder:text-content-muted"
              />
              {carregando ? (
                <p className="px-2 py-6 text-center text-sm text-content-muted">Carregando produtos…</p>
              ) : (
                <ListaProdutos grupos={grupos} rascunho={rascunho} onRascunho={setRascunho} />
              )}
              <div className="flex items-center justify-between gap-2 border-t border-theme-border pt-2">
                <button
                  type="button"
                  onClick={() => setRascunho([])}
                  disabled={rascunho.length === 0}
                  className="text-sm text-content-secondary hover:text-content-primary disabled:opacity-40"
                >
                  Limpar ({rascunho.length})
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={fechar}
                    className="rounded-lg border border-theme-border px-3 py-1.5 text-sm text-content-secondary hover:text-content-primary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={aplicar}
                    className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-accent-contrast hover:bg-accent-hover"
                  >
                    Aplicar
                  </button>
                </div>
              </div>
            </div>
          </Popover>
        )}
      </div>
      {selecionados.map((codigo) => (
        <span
          key={codigo}
          className="inline-flex max-w-[18rem] items-center gap-1 rounded-full border border-theme-border bg-surface-tertiary py-0.5 pl-2.5 pr-1 text-xs text-content-primary"
        >
          <span className="truncate" title={nomeDoProduto(codigo, catalogo)}>
            {nomeDoProduto(codigo, catalogo)}
          </span>
          <button
            type="button"
            onClick={() => onChange(selecionados.filter((c) => c !== codigo))}
            aria-label={`Tirar ${codigo} do filtro`}
            className="rounded-full p-0.5 hover:bg-surface-hover"
          >
            <span aria-hidden className="material-symbols-outlined text-[14px] leading-none">
              close
            </span>
          </button>
        </span>
      ))}
    </div>
  );
};

export default SeletorProdutos;
